// The dashboard banner's featured slides. Each slide is a row in the
// SharePoint "Featured Content" list; uploaded videos and images live in
// Shared Documents/Featured Content on the same site.
import { logActivity } from "./activityLog";
import { createListItem, deleteListItem, GRAPH_BASE, SITE_ID, updateListItem } from "./graph";
import { posterFor, type Embed } from "../components/VideoPlayer";
import type { FeaturedSlide } from "../data/featuredContent";

export const FEATURED_LIST_ID =
  import.meta.env.VITE_SP_FEATURED_LIST_ID ?? "7322573e-4731-4585-80ed-fded9998cec7";
export const FEATURED_FOLDER = "Featured Content";

export type MediaType = "Video" | "Image" | "None";

export type FeaturedRow = {
  id: string;
  title: string;
  description: string;
  tag: string;
  meta: string;
  mediaType: MediaType;
  mediaUrl: string;
  posterUrl: string;
  buttonLabel: string;
  buttonUrl: string;
  sortOrder: number;
  active: boolean;
  /** yyyy-mm-dd, or "" for no limit. */
  startDate: string;
  endDate: string;
};

export type FeaturedDraft = Omit<FeaturedRow, "id">;

// ---------- Reading ----------

const pad = (n: number) => String(n).padStart(2, "0");
export const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const text = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));
const link = (v: unknown) =>
  v && typeof v === "object" && "Url" in v ? text((v as { Url?: unknown }).Url) : text(v);
const day = (v: unknown) => {
  const t = Date.parse(text(v));
  return Number.isNaN(t) ? "" : ymd(new Date(t));
};

function mapRow(raw: { id: string; fields?: Record<string, unknown> }): FeaturedRow {
  const f = raw.fields ?? {};
  const mediaType = text(f.MediaType);
  return {
    id: raw.id,
    title: text(f.Title),
    description: text(f.Description),
    tag: text(f.Tag),
    meta: text(f.Meta),
    mediaType: mediaType === "Video" || mediaType === "Image" ? mediaType : "None",
    mediaUrl: link(f.MediaURL),
    posterUrl: link(f.PosterURL),
    buttonLabel: text(f.ButtonLabel),
    buttonUrl: link(f.ButtonURL),
    sortOrder: Number(f.SortOrder) || 0,
    active: text(f.Active) !== "No",
    startDate: day(f.StartDate),
    endDate: day(f.EndDate),
  };
}

/** Rows without a sort order go last, then by title. */
const bySortOrder = (a: FeaturedRow, b: FeaturedRow) =>
  (a.sortOrder || Infinity) - (b.sortOrder || Infinity) || a.title.localeCompare(b.title);

export async function fetchFeaturedRows(token: string): Promise<FeaturedRow[]> {
  const rows: FeaturedRow[] = [];
  let url: string | undefined = `${GRAPH_BASE}/sites/${SITE_ID}/lists/${FEATURED_LIST_ID}/items?expand=fields&$top=200`;
  while (url) {
    const res: Response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Couldn't load the banner slides (${res.status}).`);
    const data = (await res.json()) as {
      value: { id: string; fields?: Record<string, unknown> }[];
      "@odata.nextLink"?: string;
    };
    rows.push(...data.value.map(mapRow));
    url = data["@odata.nextLink"];
  }
  return rows.sort(bySortOrder);
}

export type SlideStatus = "live" | "scheduled" | "ended" | "off";

export function slideStatus(row: FeaturedDraft, today = ymd(new Date())): SlideStatus {
  if (!row.active) return "off";
  if (row.startDate && today < row.startDate) return "scheduled";
  if (row.endDate && today > row.endDate) return "ended";
  return "live";
}

// ---------- Writing ----------

const dropEmpty = (o: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== ""));

// Noon UTC lands on the same calendar day in every US time zone.
const dateField = (d: string) => (d ? `${d}T12:00:00Z` : null);
const linkField = (url: string) => (url.trim() ? { Url: url.trim(), Description: "Open" } : null);

export async function saveFeaturedRow(token: string, draft: FeaturedDraft, id?: string): Promise<string> {
  const fields: Record<string, unknown> = {
    Title: draft.title.trim(),
    Description: draft.description.trim(),
    Tag: draft.tag.trim(),
    Meta: draft.meta.trim(),
    MediaType: draft.mediaType,
    ButtonLabel: draft.buttonLabel.trim(),
    SortOrder: draft.sortOrder,
    Active: draft.active ? "Yes" : "No",
    StartDate: dateField(draft.startDate),
    EndDate: dateField(draft.endDate),
  };
  const links: Record<string, unknown> = {
    MediaURL: draft.mediaType === "None" ? null : linkField(draft.mediaUrl),
    PosterURL: draft.mediaType === "Video" ? linkField(draft.posterUrl) : null,
    ButtonURL: linkField(draft.buttonUrl),
  };

  let itemId = id;
  if (itemId) {
    await updateListItem(token, FEATURED_LIST_ID, itemId, { ...fields, ...links });
  } else {
    // Hyperlink columns are rejected on create, so they follow in a PATCH
    // (same as createLibraryItem).
    itemId = (await createListItem(token, FEATURED_LIST_ID, dropEmpty(fields))).id;
    const set = dropEmpty(links);
    if (Object.keys(set).length) await updateListItem(token, FEATURED_LIST_ID, itemId, set);
  }
  void logActivity("Feature", id ? "Banner slide updated" : "Banner slide added", draft.title, "featured", itemId);
  return itemId;
}

export async function setFeaturedActive(token: string, row: FeaturedRow, active: boolean): Promise<void> {
  await updateListItem(token, FEATURED_LIST_ID, row.id, { Active: active ? "Yes" : "No" });
  void logActivity("Feature", active ? "Banner slide turned on" : "Banner slide turned off", row.title, "featured", row.id);
}

/** Saves the given order as 1, 2, 3…, writing only rows whose number changed. */
export async function saveFeaturedOrder(token: string, ordered: FeaturedRow[]): Promise<void> {
  for (const [i, row] of ordered.entries()) {
    if (row.sortOrder !== i + 1) {
      await updateListItem(token, FEATURED_LIST_ID, row.id, { SortOrder: i + 1 });
    }
  }
}

/** Removes the slide only; its files stay in the Featured Content folder. */
export async function deleteFeaturedRow(token: string, row: FeaturedRow): Promise<void> {
  await deleteListItem(token, FEATURED_LIST_ID, row.id);
  void logActivity("Feature", "Banner slide deleted", row.title, "featured", row.id);
}

// ---------- Uploads ----------

/** Uploads to Shared Documents/Featured Content (renaming on a clash) and
 *  returns the file's SharePoint link. Sent in chunks, so large videos work. */
/** Uploads a file into a folder of the site's Documents library (renaming it
 *  if the name is taken) and returns its link. */
export async function uploadToFolder(
  token: string,
  folder: string,
  file: Blob,
  name: string,
  onProgress?: (fraction: number) => void
): Promise<{ webUrl: string; name: string }> {
  if (!file.size) throw new Error(`"${name}" is empty.`);
  const safe = name.replace(/[\\/:*?"<>|#%]+/g, "-").trim() || "file";
  const start = await fetch(
    `${GRAPH_BASE}/sites/${SITE_ID}/drive/root:/${encodeURIComponent(folder)}/${encodeURIComponent(safe)}:/createUploadSession`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ item: { "@microsoft.graph.conflictBehavior": "rename" } }),
    }
  );
  if (!start.ok) throw new Error(`Couldn't start uploading "${safe}" (${start.status}).`);
  const { uploadUrl } = (await start.json()) as { uploadUrl: string };

  const CHUNK = 320 * 1024 * 20; // Graph wants multiples of 320 KiB
  let last: Response | null = null;
  for (let from = 0; from < file.size; from += CHUNK) {
    const to = Math.min(from + CHUNK, file.size);
    // The upload URL is pre-authorized: no Authorization header here.
    last = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Range": `bytes ${from}-${to - 1}/${file.size}` },
      body: file.slice(from, to),
    });
    if (!last.ok) throw new Error(`Uploading "${safe}" failed (${last.status}).`);
    onProgress?.(to / file.size);
  }
  return (await last!.json()) as { webUrl: string; name: string };
}

export async function uploadFeaturedFile(
  token: string,
  file: Blob,
  name: string,
  onProgress?: (fraction: number) => void
): Promise<string> {
  const item = await uploadToFolder(token, FEATURED_FOLDER, file, name, onProgress);
  void logActivity("Feature", "Banner file uploaded", item.name, "featured");
  return item.webUrl;
}

// ---------- Showing SharePoint media ----------
//
// SharePoint files need the viewer's sign-in, so they can't go straight into
// <img>/<video>. Images are fetched with the Graph token and shown as blob
// URLs; videos play in SharePoint's own player (Stream), which streams any
// size and handles seeking.

export const isSharePointUrl = (url: string) => /^https:\/\/[^/]+\.sharepoint\.com\//i.test(url);

type DriveItem = {
  id: string;
  parentReference?: { driveId?: string };
  sharepointIds?: { listItemUniqueId?: string; siteUrl?: string };
};

const shareId = (url: string) =>
  "u!" +
  btoa(String.fromCharCode(...new TextEncoder().encode(url)))
    .replace(/=+$/, "")
    .replace(/\//g, "_")
    .replace(/\+/g, "-");

async function graphJson<T>(token: string, url: string): Promise<T> {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Graph ${res.status}`);
  return (await res.json()) as T;
}

const driveItem = (token: string, url: string) =>
  graphJson<DriveItem>(token, `${GRAPH_BASE}/shares/${shareId(url)}/driveItem?$select=id,parentReference,sharepointIds`);

const itemPath = (item: DriveItem) => `${GRAPH_BASE}/drives/${item.parentReference?.driveId}/items/${item.id}`;

// One lookup per link per page load, however many components ask.
const cache = new Map<string, Promise<unknown>>();
function once<T>(key: string, load: () => Promise<T>): Promise<T> {
  let p = cache.get(key) as Promise<T> | undefined;
  if (!p) {
    p = load();
    p.catch(() => cache.delete(key));
    cache.set(key, p);
  }
  return p;
}

/** An image link the page can show: SharePoint files become blob URLs. */
export function resolveImage(token: string, url: string): Promise<string> {
  if (!isSharePointUrl(url)) return Promise.resolve(url);
  return once(`img:${url}`, async () => {
    const item = await driveItem(token, url);
    const res = await fetch(`${itemPath(item)}/content`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Graph ${res.status}`);
    return URL.createObjectURL(await res.blob());
  });
}

/** How to play a SharePoint video, plus SharePoint's own thumbnail for it. */
function resolveSharePointVideo(token: string, url: string): Promise<{ embed: Embed | null; thumb?: string }> {
  return once(`video:${url}`, async () => {
    const item = await driveItem(token, url);
    const { siteUrl, listItemUniqueId } = item.sharepointIds ?? {};
    const embed: Embed | null =
      siteUrl && listItemUniqueId
        ? {
            kind: "iframe",
            src:
              `${siteUrl}/_layouts/15/embed.aspx?UniqueId=${listItemUniqueId}` +
              `&embed=${encodeURIComponent(JSON.stringify({ hv: "CopyEmbedCode" }))}` +
              "&referrer=StreamWebApp&referrerScenario=EmbedDialog.Create",
          }
        : null;
    const thumbs = await graphJson<{ value: { large?: { url?: string } }[] }>(
      token,
      `${itemPath(item)}/thumbnails?$select=large`
    ).catch(() => null);
    return { embed, thumb: thumbs?.value[0]?.large?.url };
  });
}

/** The still to show for a row: its image, its poster, or the video's own thumbnail. */
export async function previewImage(token: string, row: FeaturedDraft): Promise<string | undefined> {
  if (row.mediaType === "Image") return row.mediaUrl ? resolveImage(token, row.mediaUrl) : undefined;
  if (row.mediaType !== "Video") return undefined;
  if (row.posterUrl) return resolveImage(token, row.posterUrl);
  if (row.mediaUrl && isSharePointUrl(row.mediaUrl)) return (await resolveSharePointVideo(token, row.mediaUrl)).thumb;
  return posterFor(row.mediaUrl);
}

/** A row as the dashboard banner shows it. Media that can't be loaded is
 *  left out rather than failing the whole banner. */
export async function toSlide(token: string, id: string, row: FeaturedDraft): Promise<FeaturedSlide> {
  const slide: FeaturedSlide = {
    id,
    title: row.title,
    description: row.description || undefined,
    tag: row.tag || undefined,
    meta: row.meta || undefined,
    textOnly: row.mediaType === "None",
    link: row.buttonUrl ? { label: row.buttonLabel || "Learn more", url: row.buttonUrl } : undefined,
  };
  slide.image = await previewImage(token, row).catch(() => undefined);
  if (row.mediaType === "Video" && row.mediaUrl) {
    slide.videoUrl = row.mediaUrl;
    if (isSharePointUrl(row.mediaUrl)) {
      // null: no in-page player, so the video opens in SharePoint instead.
      slide.player = (await resolveSharePointVideo(token, row.mediaUrl).catch(() => null))?.embed ?? null;
    }
  }
  return slide;
}
